<script>
	// NPM Modules
	import collect from 'collect.js';
	import { createEventDispatcher } from 'svelte';
	// Stores
	import SportStore from '@/Stores/Sport/SportStore';
	import MarketTypeStore from '@/Stores/Sport/MarketTypeStore';
	import MultiGroupStore from '@/Stores/Sport/MultiGroupStore';
	// Components
	import Icon from '@/Components/Icon.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import PillMultiSelect from '@/Components/PillMultiSelect.svelte';
	// Services
	import { removeUrlParameter, setUrlParameter } from '@/Services/Helpers';
	import { notify } from '@/Services/Notify';
	import LoadingButton from '@/Components/LoadingButton.svelte';

	const dispatch = createEventDispatcher();

	export let sport;

	let sending = false;
	let selectedMarketType;
	let selectedSport = sport.id;

	$: selectedSport && selectedSportChanged();
	$: marketTypes = $MarketTypeStore && sortMarketTypes();
	$: multiGroups = $MultiGroupStore && $MultiGroupStore.sortBy('name').all();

	function sortMarketTypes() {
		return $MarketTypeStore

			.sort(
				(a, b) =>
					(b &&
						b.multi_excluded_market_types &&
						b.multi_excluded_market_types.data &&
						b.multi_excluded_market_types.data.count()) +
						(b && b.multi_excluded_groups && b.multi_excluded_groups.data && b.multi_excluded_groups.data.count()) -
						((a && a.multi_excluded_groups && a.multi_excluded_groups.data && a.multi_excluded_groups.data.count()) +
							(a &&
								a.multi_excluded_market_types &&
								a.multi_excluded_market_types.data &&
								a.multi_excluded_market_types.data.count())) || a.name - b.name
			)
			.all();
	}

	function getMarketTypes() {
		MarketTypeStore.getMarketTypesForSport(sport.id, ['multi_excluded_market_types', 'multi_excluded_groups']);
		MultiGroupStore.getMultiGroupsForSport(sport.id);
	}

	async function selectedSportChanged() {
		await MarketTypeStore.getMarketTypesForSport(selectedSport, [
			'multi_excluded_market_types',
			'multi_excluded_groups'
		]);
		await MultiGroupStore.getMultiGroupsForSport(selectedSport);
	}

	function changeSport(sport_id) {
		if (typeof parseInt(sport_id) !== 'undefined' && sport_id !== false) {
			setUrlParameter('sport', sport_id);
			selectedSport = sport_id;
		} else {
			removeUrlParameter('sport');
			selectedSport = false;
		}
	}

	function addToSelectedMarketType(marketType) {
		selectedMarketType.multi_excluded_market_types.data = selectedMarketType.multi_excluded_market_types.data.push({
			id: marketType.id,
			name: marketType.name,
			sport: {
				id: marketType.sport.id,
				name: marketType.sport.name
			}
		});
	}

	function removeFromSelectedMarketType(marketTypeId) {
		selectedMarketType.multi_excluded_market_types.data = selectedMarketType.multi_excluded_market_types.data.reject(
			(market_type) => {
				return market_type.id == marketTypeId;
			}
		);
	}

	function addGroupToSelectedMarketType(multiGroup) {
		selectedMarketType.multi_excluded_groups.data = selectedMarketType.multi_excluded_groups.data.push({
			id: multiGroup.id,
			name: multiGroup.name,
			sport: {
				id: multiGroup.sport.id,
				name: multiGroup.sport.name
			}
		});
	}

	function removeGroupFromSelectedMarketType(multiGroupId) {
		selectedMarketType.multi_excluded_groups.data = selectedMarketType.multi_excluded_groups.data.reject(
			(multiGroup) => {
				return multiGroup.id == multiGroupId;
			}
		);
	}

	function updateMarketType(marketType) {
		sending = true;
		const payload = {
			multi_excluded_market_types: [],
			multi_excluded_groups: []
		};

		marketType.multi_excluded_market_types.data.each((excludedMarketType) => {
			payload.multi_excluded_market_types.push(excludedMarketType.id);
		});
		marketType.multi_excluded_groups.data.each((excludedMultiGroup) => {
			payload.multi_excluded_groups.push(excludedMultiGroup.id);
		});
		marketType
			.patch(payload)
			.then(() => {
				notify({
					type: 'success',
					title: 'Market type successfully updated',
					message: `${marketType.name} successfully updated!`,
					timeout: 3
				});
			})
			.catch(() => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: 'Problem updating market type',
					timeout: 3
				});
			})
			.finally(() => (sending = false));
	}
</script>

{#if !selectedMarketType}
	<div>
		<div class="flex justify-center items-center mt-1">
			<h1>Select market type to edit multi exclusions</h1>
		</div>
		<table class="w-full whitespace-nowrap mt-3">
			<thead>
				<tr class="text-xs text-left text-gray-500 uppercase">
					<th class="px-6 pt-3 pb-2 font-medium">Market Types</th>
					<th class="w-px px-6 pt-3 pb-2 font-medium text-left">Label</th>
					<th class="w-px px-6 pt-3 pb-2 font-medium text-left">Types Excluded</th>
					<th class="w-px px-6 pt-3 pb-2 font-medium text-left">Groups Excluded</th>
					<th class="w-px px-6 pt-3 pb-2 font-medium text-left">Sport</th>
					<th class="w-px px-6 pt-3 pb-2 font-medium text-right" />
				</tr>
			</thead>
			<tbody class="bg-white">
				{#each marketTypes as marketType (marketType.id)}
					<tr
						on:click={() => {
							selectedMarketType = marketType;
							dispatch('selected');
						}}
						class="cursor-pointer hover:bg-gray-100 focus-within:bg-gray-100"
					>
						<td class="p-4 px-6 border-t">
							<div class="flex items-center">
								<div class="mr-2 font-semibold">{marketType.name}</div>
							</div>
						</td>
						<td class="px-6 border-t">
							{marketType.label ?? ''}
						</td>
						<td class="px-6 border-t">
							{(marketType?.multi_excluded_market_types?.data?.count() ?? 0) === 0
								? ''
								: marketType?.multi_excluded_market_types?.data.count()}
						</td>
						<td class="px-6 border-t">
							{(marketType?.multi_excluded_groups?.data?.count() ?? 0) === 0
								? ''
								: marketType?.multi_excluded_groups?.data?.count()}
						</td>
						<td class="w-px px-6 border-t">
							<div class="px-2 py-1 bg-green-500 rounded-md text-light">
								{marketType.sport.name}
							</div>
						</td>
						<td class="w-px px-6 border-t">
							<Icon name="chevron-right" classes="block w-4 h-4 text-gray-400 inline" />
						</td>
					</tr>
				{:else}
					<tr>
						<td class="px-6 py-4 text-center border-t" colspan="9">
							No market types found for {sport.name}.
						</td>
					</tr>
				{/each}
			</tbody>
		</table>
	</div>
{:else}
	<div class="w-full mt-3">
		<div class="sticky top-0 z-10 flex py-2">
			<div class="flex flex-col flex-grow">
				<div class="flex justify-between">
					<div>
						<div class="flex flex-row items-center">
							<button
								title={'Market types list'}
								class="flex cursor-pointer hover:underline text-grey items-center"
								on:click={() => {
									getMarketTypes();
									selectedMarketType = undefined;
								}}
							>
								<Icon name="chevron-left" classes="w-2 mr-2" />
								<span class="mr-2 text-lg font-bold whitespace-nowrap">Market types</span>
							</button>
						</div>
					</div>
					<div>
						<h2 class="pt-2 text-xl font-bold text-center">
							Multi control - {selectedMarketType.name}
						</h2>
					</div>
					<div>
						<div class="flex flex-row items-center">
							<LoadingButton
								loading={sending}
								on:click={() => updateMarketType(selectedMarketType)}
								class="btn btn-primary"
							>
								Save changes
							</LoadingButton>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="flex w-full text-center bg-white">
			<!-- /Page Header -->
			{#if $SportStore && $MultiGroupStore}
				<div class="flex m-2 rounded w-full min-h-104">
					<div class="flex w-full space-x-2">
						<!-- MARKET TYPES SELECTED -->
						<div class="w-2/3">
							<div class="flex items-center w-full mb-2 space-x-4">
								<div class="font-semibold">
									Market types excluded from being in a multi with "{selectedMarketType.name}" ({(selectedMarketType
										.multi_excluded_market_types.data && selectedMarketType.multi_excluded_market_types.data.count()) +
										(selectedMarketType.multi_excluded_groups.data &&
											selectedMarketType.multi_excluded_groups.data.count()) ?? 0})
								</div>
								<button
									on:click={() => {
										selectedMarketType.multi_excluded_market_types.data = collect([]);
										selectedMarketType.multi_excluded_groups.data = collect([]);
									}}
									class="py-1 -mb-1 text-xs font-normal btn btn-primary-action"
								>
									Clear
								</button>
							</div>
							<div class="w-full p-1 overflow-y-auto bg-gray-200 border">
								{#if selectedMarketType.multi_excluded_market_types.data && selectedMarketType.multi_excluded_market_types.data.count() === 0 && selectedMarketType.multi_excluded_groups.data && selectedMarketType.multi_excluded_groups.data.count() === 0}
									<div class="py-4 mx-2 text-center">Add some market types/multi groups...</div>
								{/if}
								{#each (selectedMarketType.multi_excluded_market_types.data && selectedMarketType.multi_excluded_market_types.data.all()) ?? [] as marketTypeExclusions}
									<div class="flex items-center w-full space-x-2">
										<PillMultiSelect
											action="remove"
											on:remove={() => removeFromSelectedMarketType(marketTypeExclusions.id)}
											bgColour={'bg-green-500'}
											label={`${marketTypeExclusions.name} ${
												marketTypeExclusions.label ? '-' + marketTypeExclusions.label : ''
											}  [${marketTypeExclusions.sport.name}]`}
										/>
									</div>
								{/each}
								{#each (selectedMarketType.multi_excluded_groups.data && selectedMarketType.multi_excluded_groups.data.all()) ?? [] as multiExclusionGroup}
									<div class="flex items-center w-full space-x-2">
										<PillMultiSelect
											action="remove"
											on:remove={() => removeGroupFromSelectedMarketType(multiExclusionGroup.id)}
											bgColour={'bg-orange-500'}
											label={`${multiExclusionGroup.name} [${multiExclusionGroup.sport.name}]`}
										/>
									</div>
								{/each}
							</div>
						</div>
						<!-- AVAILABLE MARKET TYPES AND MULTI GROUPS TO SELECT FROM -->
						<div class="w-1/3 my-2">
							<div class="flex flex-col items-center justify-start w-full mb-2">
								<div class="flex w-full justify-start text-left">
									<SelectInput
										label="Select a sport"
										className="py-2 w-full"
										name="sport"
										required={true}
										bind:value={selectedSport}
										on:change={(event) => changeSport(event.target.value)}
									>
										<option value={false}>Select Sport...</option>
										{#each $SportStore.sortBy('name').all() as sport (sport.id)}
											{#if parseInt(sport.id) === parseInt(selectedSport)}
												<option value={sport.id} selected>{sport.name}</option>
											{:else}
												<option value={sport.id}>{sport.name}</option>
											{/if}
										{/each}
									</SelectInput>
								</div>
								<div class="font-semibold">
									Available Market Types
									{#if selectedSport}
										({`${
											$MarketTypeStore.count() -
											((selectedMarketType.multi_excluded_market_types.data &&
												selectedMarketType.multi_excluded_market_types.data.count()) ??
												0)
										}/${$MarketTypeStore.count()}`})
									{/if}
								</div>
							</div>
							<div class="w-full p-2 overflow-y-auto bg-gray-200 border" style="max-height: 225px;">
								{#if selectedSport}
									{#each marketTypes as marketType}
										<!-- ONLY SHOW THE MARKET TYPE IF IT HAS NOT ALREADY BEEN ADDED -->
										{#if !(((selectedMarketType.multi_excluded_market_types.data && selectedMarketType.multi_excluded_market_types.data
														.pluck('id')
														.all()) ?? []).includes(marketType.id) || marketType.id == selectedMarketType.id)}
											<div class="w-full">
												<PillMultiSelect
													on:add={() => addToSelectedMarketType(marketType)}
													bgColour={'bg-green-500'}
													label={marketType.name}
												/>
											</div>
										{/if}
									{:else}
										No market types found!
									{/each}
								{/if}
							</div>
							<div class="flex flex-col items-center justify-start w-full mb-2">
								<div class="font-semibold">
									Available Multi Groups
									{#if selectedSport}
										({`${
											$MultiGroupStore.count() -
											((selectedMarketType.multi_excluded_groups.data &&
												selectedMarketType.multi_excluded_groups.data.count()) ??
												0)
										}/${$MultiGroupStore.count()}`})
									{/if}
								</div>
							</div>
							<div class="w-full p-2 overflow-y-auto bg-gray-200 border" style="max-height: 225px;">
								{#if selectedSport}
									{#each multiGroups as multiGroup}
										<!-- ONLY SHOW THE MULTI GROUP IF IT HAS NOT ALREADY BEEN ADDED -->
										{#if !((selectedMarketType.multi_excluded_groups.data && selectedMarketType.multi_excluded_groups.data
													.pluck('id')
													.all()) ?? []).includes(multiGroup.id)}
											<div class="w-full">
												<PillMultiSelect
													on:add={() => addGroupToSelectedMarketType(multiGroup)}
													bgColour={'bg-orange-500'}
													label={multiGroup.name}
												/>
											</div>
										{/if}
									{:else}
										No multi groups found!
									{/each}
								{/if}
							</div>
						</div>
					</div>
				</div>
			{/if}
		</div>
	</div>
{/if}
